import { makeAutoObservable, runInAction } from "mobx";
import { ChangeEvent, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { ANALYTICS_EVENTS } from "../../../analytics-store.tsx";
import { IGroupOption } from "../../../components/common/CustomSingleSelectDropdown/SingleSelectGroupedDropDown.tsx";
import {
	ModelDropdownKey,
	getModelKey,
	getModelName,
	getModelVersion,
} from "../../../components/common/DropDowns/TextToImageModelsDropdown/NewTextToImageModelsDropdown.tsx";
import { TextToImageModels } from "../../../components/common/DropDowns/TextToImageModelsDropdown/TextToImageModelsDropdown.tsx";
import { BackendError } from "../../../config/axios.tsx";
import { APPS } from "../../../constants/AppsConstants.ts";
import { FIBO_DEFAULT_NUM_RESULTS } from "../../../constants/FiboConstants.ts";
import { isDnaiOrg } from "../../../constants/OrgConstants.ts";
import { POSTHOG_EVENTS } from "../../../constants/PostHogEvents";
import { IMAGE_REFERENCE, IMAGE_REFERENCE_DISABLED_MODELS } from "../../../constants/TextToImageConstants.ts";
import { BRIA_QUALCOMM_SERVER_HOST } from "../../../constants/common.ts";
import useErrorToast from "../../../hooks/useErrorToast.tsx";
import { IRootStore } from "../../../mobx/root-store.tsx";
import { PlaygroundImage, PlaygroundResult } from "../../../models/image-to-image.ts";
import { SyntheticEventProps, TextToImageConfigType, modelTypes } from "../../../models/text-to-image.ts";
import postHogService from "../../../services/PostHogService";
import { getLocalQualcommAvailableModels, isFiboApp } from "../../../utils";
import QueryService from "../../../utils/QueryService.ts";
import iframeStore from "../../IframeNew/iframe-store.tsx";

export interface ITextToImageStore {
	config: TextToImageConfigType;
	models: TextToImageModels;
	loadingModels: boolean;
	mediums: string[];
	loadingMediums: boolean;
	errors: { generate?: BackendError };
	usedSeeds: number[];
	handleConfigChange: <K extends keyof TextToImageConfigType>(key: K, value: TextToImageConfigType[K]) => void;
	getModels: () => Promise<TextToImageModels>;
	getMediums: () => Promise<string[]>;
	generateTextToImage: (config?: TextToImageConfigType, isTgEvaluation?: boolean) => Promise<void>;
	clearConfig: () => void;
	handleText2ImagePopupChange: (e: ChangeEvent<HTMLInputElement>, selectedOption: IGroupOption) => void;
	handleUploadImage: (e: ChangeEvent<HTMLInputElement> | SyntheticEventProps) => Promise<void>;
	imageReference: PlaygroundImage;
	isImageReferenceEnabled: () => boolean;
	isUploadingImageReference: boolean;
	abortImageGeneration: () => void;
	isGeneratingImages: boolean;
	imageGenerationContainerRef: React.MutableRefObject<HTMLDivElement | null>;
	controlNetAspectRatioError: boolean;

	handleDeleteImageReference(): Promise<void>;

	IpSignalWarning: boolean;
	isRefining: boolean;
	imageReferenceContentModerationError: boolean;
	refineImage: (image: PlaygroundImage) => Promise<void>;
}

export default class TextToImageStore implements ITextToImageStore {
	private queryService: QueryService = new QueryService("/text-to-image");
	private dnaiPromptEnhancerQueryService: QueryService = new QueryService(
		"/text-to-image/dnai-prompt-enhancer",
		true,
	);

	private abortController: AbortController = new AbortController();
	isGeneratingImages = false;
	rootStore: IRootStore;
	config: TextToImageConfigType = defaultTextToImageConfig;
	models: TextToImageModels = {};
	loadingModels: boolean = false;
	mediums: string[] = [];
	usedSeeds: number[] = [];
	loadingMediums: boolean = false;
	errorToast = useErrorToast();
	errors: { generate?: BackendError } = {};
	imageReference: PlaygroundImage = { id: "", url: "", playground_result_id: "" };
	isUploadingImageReference = false;
	IpSignalWarning = false;
	isRefining = false;
	imageReferenceContentModerationError = false;
	// validation error for ControlNet images in evaluation playground (mismatched aspect ratios)
	controlNetAspectRatioError: boolean = false;
	imageGenerationContainerRef: React.MutableRefObject<HTMLDivElement | null> = useRef<HTMLDivElement | null>(null);

	private imageGenerationsQueryService: QueryService = new QueryService("/image_generations");

	constructor(rootStore: IRootStore) {
		makeAutoObservable(this);
		this.rootStore = rootStore;
	}

	isImageReferenceEnabled = () => {
		return this.imageReference && this.imageReference.url.trim().length > 0;
	};

	private generateTailoredV2Multi = async (opts?: {
		textToImageConfig?: TextToImageConfigType;
		isTgEvaluation?: boolean;
		isRefine?: boolean;
		image?: PlaygroundImage;
	}): Promise<void> => {
		const { textToImageConfig, isTgEvaluation, isRefine, image } = opts || {};
		const config = textToImageConfig || this.config;

		runInAction(() => {
			this.errors = { ...this.errors, generate: undefined };
		});

		const { playgroundStore } = this.rootStore;
		const isTg = playgroundStore.selectedConfig === APPS.TG_IMAGE_GENERATION;
		const startTime = Date.now();
		const modelName = config.model_name || `tailored ${config.tailored_model_id}`;
		this.abortController = new AbortController();
		const savedIndex = playgroundStore.playgroundResults.length;
		const numResults = config.num_results || 1;

		const resultsSkeletons: PlaygroundResult = {
			id: uuidv4(),
			config: { ...config, is_refine: isRefine },
			type: isTg ? APPS.TG_IMAGE_GENERATION : APPS.TEXT_TO_IMAGE,
			images: Array.from({ length: numResults }).map(() => ({
				id: uuidv4(),
				url: "",
				seed: NaN,
				loading: true,
				playground_result_id: "",
			})),
		};
		playgroundStore.playgroundResults = [...playgroundStore.playgroundResults, resultsSkeletons];

		try {
			runInAction(() => {
				this.isGeneratingImages = true;
				this.IpSignalWarning = false;
			});

			const baseSeed =
				isRefine && image?.seed !== undefined && image?.seed !== null
					? parseInt(image.seed!.toString())
					: config.seed !== undefined && config.seed !== null
						? parseInt(config.seed.toString())
						: Math.floor(1000000000 + Math.random() * 1000000000);

			await Promise.all(
				Array.from({ length: numResults }).map(async (_v, index) => {
					const seed = baseSeed + index;
					const effectivePrompt = isRefine
						? (image?.refine_prompt ?? (config as any).refine_prompt ?? config.prompt)
						: config.prompt;
					const effectiveStructuredPrompt =
						isRefine && image?.structured_prompt ? image.structured_prompt : config.structured_prompt;

					const payload: any = {
						tailored_model_id: config.tailored_model_id,
						tailored_model_influence: config.tailored_model_influence,
						prompt: effectivePrompt,
						structured_prompt: effectiveStructuredPrompt,
						negative_prompt: config.negative_prompt,
						guidance_scale: (config as any).text_guidance_scale,
						aspect_ratio: config.aspect_ratio,
						steps_num: config.steps_num,
						seed,
						sync: false,
						prompt_content_moderation: true,
						visual_output_content_moderation: true,
						enhance_image: config.enhance_image,
					};

					const response = await this.imageGenerationsQueryService.post(
						"/tailored",
						payload,
						{ "Content-Type": "application/json" },
						{ signal: this.abortController.signal },
					);

					const current = playgroundStore.playgroundResults[savedIndex].images[index];
					if (response?.status_url) {
						runInAction(() => {
							(playgroundStore.playgroundResults[savedIndex].images[index] as any) = {
								...current,
								status_url: response.status_url,
								config: playgroundStore.playgroundResults[savedIndex].config,
								type: isTg ? APPS.TG_IMAGE_GENERATION : APPS.TEXT_TO_IMAGE,
								aspect_ratio: config.aspect_ratio,
								ui_type: isTgEvaluation ? "Tailored_gen_evaluation_interface" : undefined,
							};
						});
					} else if (response?.image_url) {
						runInAction(() => {
							(playgroundStore.playgroundResults[savedIndex].images[index] as any) = {
								...current,
								url: response.image_url,
								seed: response.seed,
								structured_prompt: response.structured_prompt,
								warning: response.warning,
								loading: false,
								config: playgroundStore.playgroundResults[savedIndex].config,
								type: isTg ? APPS.TG_IMAGE_GENERATION : APPS.TEXT_TO_IMAGE,
								aspect_ratio: config.aspect_ratio,
								ui_type: isTgEvaluation ? "Tailored_gen_evaluation_interface" : undefined,
							};
						});
					} else {
						throw new Error("Unexpected engine response");
					}
				}),
			);

			const generationTime = Date.now() - startTime;
			postHogService.trackEvent(
				POSTHOG_EVENTS.IMAGE_GENERATION_COMPLETED,
				{ model: modelName, generation_time: generationTime, image_count: numResults, version: "v2" },
				{ send_instantly: true },
			);

			runInAction(() => {
				this.isGeneratingImages = false;
			});
			this.rootStore.playgroundUserToOrgConversionStore.incrementGenerationCount();
		} catch (err: any) {
			this.isGeneratingImages = false;
			postHogService.trackEvent(
				POSTHOG_EVENTS.IMAGE_GENERATION_FAILED,
				{
					model: modelName,
					error: err.response?.data?.message || err.message || "Unknown error",
					version: "v2",
				},
				{ send_instantly: true },
			);
			runInAction(() => {
				this.errors.generate = {
					message: err.response?.data?.message || err.message || "Image generation failed",
					status: err.response?.status || 500,
				};
				playgroundStore.playgroundResults = playgroundStore.playgroundResults.filter(
					(_, index) => index !== savedIndex,
				);
			});
			return Promise.reject(err);
		}
	};

	clearConfig = () => {
		this.config = defaultTextToImageConfig;
		this.imageReference = { id: "", url: "", playground_result_id: "" };
		this.controlNetAspectRatioError = false;
	};

	handleConfigChange = <K extends keyof TextToImageConfigType>(key: K, value: TextToImageConfigType[K]) => {
		runInAction(() => {
			this.config[key] = value;
		});
	};
	handleBulkConfigChange = <K extends keyof TextToImageConfigType>(
		keyOrChanges: K | Partial<TextToImageConfigType>,
		value?: TextToImageConfigType[K],
	) => {
		runInAction(() => {
			if (typeof keyOrChanges === "string") {
				this.config[keyOrChanges] = value as TextToImageConfigType[K];
			} else {
				this.config = { ...this.config, ...keyOrChanges };
			}
		});
	};

	abortImageGeneration = () => {
		if (this.abortController) {
			this.abortController.abort();
			this.rootStore.playgroundStore.removeNotLoadingImages();
		}
		runInAction(() => {
			this.IpSignalWarning = false;
		});
	};

	/**
	 * Generate images using the text-to-image API.
	 *
	 * If `config.model` is `fibo`, it will use the generate v2 from fiboStore.
	 */
	generateTextToImage = async (
		textToImageConfig?: TextToImageConfigType,
		isTgEvaluation?: boolean,
	): Promise<void> => {
		this.config.is_refine = false;

		if (this.config.model === "fibo") {
			return await this.rootStore.fiboStore.generate({ isTgEvaluation, skipGenerateStructuredPrompt: true });
		}

		if (
			this.config?.training_version === "fibo" &&
			(textToImageConfig?.tailored_model_id || this.config.tailored_model_id)
		) {
			return await this.generateTailoredV2Multi({ textToImageConfig, isTgEvaluation });
		}

		const config = textToImageConfig || this.config;
		runInAction(() => {
			this.errors = { ...this.errors, generate: undefined };
		});
		const { playgroundStore } = this.rootStore;
		const isTg = playgroundStore.selectedConfig === APPS.TG_IMAGE_GENERATION;

		const startTime = Date.now();
		const modelName = config.model_name || `${config.model} ${config.model_version}`;
		this.abortController = new AbortController();
		const savedIndex = playgroundStore.playgroundResults.length;
		const resultsSkeletons: PlaygroundResult = {
			id: uuidv4(),
			config: { ...config },
			type: isTg ? APPS.TG_IMAGE_GENERATION : APPS.TEXT_TO_IMAGE,
			images: Array.from({ length: config.num_results! }).map((_) => ({
				id: "",
				url: "",
				seed: NaN,
				loading: true,
				playground_result_id: "",
			})),
		};
		playgroundStore.playgroundResults = [...playgroundStore.playgroundResults, resultsSkeletons];

		try {
			const formData = new FormData();
			runInAction(() => {
				this.isGeneratingImages = true;
				this.IpSignalWarning = false;
			});

			this.imageReference.file && formData.append("file", this.imageReference.file);
			const configData = {
				...config,
				prompt: config.generation_prefix
					? config.generation_prefix.trimEnd() + " " + config.prompt
					: config.prompt,
				num_results: config.num_results,
			};

			postHogService.trackEvent(
				POSTHOG_EVENTS.IMAGE_GENERATION_STARTED,
				{
					model: modelName,
					prompt: configData.prompt,
				},
				{ send_instantly: true },
			);

			const callDnaiPromptEnhancer = !!config.prompt && !!config.prompt_enhancement && isDnaiOrg();
			if (callDnaiPromptEnhancer) {
				try {
					const promptEnhancerRes = await this.dnaiPromptEnhancerQueryService.post(
						"/enhance-prompt",
						{ prompt: config.prompt },
						undefined,
						{ signal: this.abortController.signal },
					);
					configData.prompt = promptEnhancerRes.enhanced_prompt;
					(playgroundStore.playgroundResults[savedIndex].config as TextToImageConfigType).prompt =
						promptEnhancerRes.enhanced_prompt;
					configData.prompt_enhancement = false;
				} catch {
					/* empty */
				}
			}

			formData.append("config", JSON.stringify(configData));

			let generatedImages: PlaygroundImage[] = [];
			const qualcommModelList = await getLocalQualcommAvailableModels();
			const modelId = config.model_id || getModelKey(config.model, config.model_version);
			if (qualcommModelList && qualcommModelList.includes(modelId as string)) {
				const qualcommQueryService = new QueryService("/generate", undefined, BRIA_QUALCOMM_SERVER_HOST);
				const qualcommRes = await qualcommQueryService.post(
					"/",
					{ ...configData },
					{ "Content-Type": "application/json" },
					{ signal: this.abortController.signal },
				);
				generatedImages = qualcommRes.result.map(
					(res: any) =>
						({
							seed: res.seed,
							url: `${BRIA_QUALCOMM_SERVER_HOST}${res.urls[0]}`,
							warning: res.warning,
						}) as PlaygroundImage,
				);
			} else {
				generatedImages = await this.queryService.post(
					"/",
					formData,
					{ "Content-Type": "multipart/form-data" },
					{ signal: this.abortController.signal },
				);
			}
			iframeStore.sendGeneratePostMessage(config, generatedImages, false);

			const generationTime = Date.now() - startTime;

			postHogService.trackEvent(
				POSTHOG_EVENTS.IMAGE_GENERATION_COMPLETED,
				{
					model: modelName,
					generation_time: generationTime,
					image_count: generatedImages.length,
				},
				{ send_instantly: true },
			);

			runInAction(() => {
				this.isGeneratingImages = false;
				this.IpSignalWarning = generatedImages.some((image) => !!image.warning);
				playgroundStore.playgroundResults[savedIndex].config = {
					...playgroundStore.playgroundResults[savedIndex].config,
					seed: generatedImages[0].seed,
				} as TextToImageConfigType;
				playgroundStore.playgroundResults[savedIndex].images = generatedImages.map((image) => ({
					...image,
					loading: true,
					id: uuidv4(),
					config: playgroundStore.playgroundResults[savedIndex].config,
					type: isTg ? APPS.TG_IMAGE_GENERATION : APPS.TEXT_TO_IMAGE,
					aspect_ratio: this.imageReference?.file ? "auto " : config.aspect_ratio,
					ui_type: isTgEvaluation ? "Tailored_gen_evaluation_interface" : undefined,
				}));
				this.logGenerateTextToImage();
			});
			this.rootStore.playgroundUserToOrgConversionStore.incrementGenerationCount();
		} catch (err: any) {
			this.isGeneratingImages = false;

			postHogService.trackEvent(
				POSTHOG_EVENTS.IMAGE_GENERATION_FAILED,
				{
					model: modelName,
					error: err.response?.data?.message || "Unknown error",
					error_status: err.response?.status,
				},
				{ send_instantly: true },
			);

			runInAction(() => {
				this.errors.generate = {
					message: err.response.data.message,
					status: err.response.status,
				};
			});
			runInAction(() => {
				playgroundStore.playgroundResults = playgroundStore.playgroundResults.filter(
					(_, index) => index !== savedIndex,
				);
			});
			return Promise.reject(err);
		}
	};

	logGenerateTextToImage = () => {
		const { config, isImageReferenceEnabled } = this;
		const { logEvent } = this.rootStore.analyticsStore;
		const modelName = this.config.model_name;
		logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_GENERATE, { model_name: modelName });

		if (isImageReferenceEnabled()) {
			logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_IMAGE_REFERENCE, { model_name: modelName });
		}

		if (config.tailored_model_id) {
			logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_TAILORED_MODELS, {
				tailoredModelId: config.tailored_model_id,
				model_name: modelName,
			});
			return;
		}

		const modelEventMap = {
			[modelTypes.Fast]: ANALYTICS_EVENTS.IMAGE_GENERATION_BRIA_FAST,
			[modelTypes.Base]: ANALYTICS_EVENTS.IMAGE_GENERATION_BRIA_BASE,
			[modelTypes.Hd]: ANALYTICS_EVENTS.IMAGE_GENERATION_BRIA_HD,
		};

		const modelEvent = modelEventMap[config.model as unknown as keyof typeof modelEventMap];
		if (modelEvent) {
			logEvent(modelEvent, { model_name: modelName });
		}
	};

	getModels = async (): Promise<TextToImageModels> => {
		try {
			if (Object.keys(this.models).length === 0) {
				this.loadingModels = true;
				const models: TextToImageModels = await this.queryService.get("/models");
				runInAction(() => {
					this.loadingModels = false;
					this.models = models;
				});
			}

			const iframeEnabledModels = iframeStore.iframe?.config.image_generation_config?.enabled_foundation_models;
			if (iframeEnabledModels)
				this.models = Object.fromEntries(
					Object.entries(this.models)
						.filter(([type]) => type in iframeEnabledModels)
						.map(([type, details]) => [
							type,
							{
								...details,
								versions: details.versions.filter((version) => version in iframeEnabledModels[type]),
							},
						]),
				);

			return this.models;
		} catch (e: any) {
			this.loadingModels = false;
			return Promise.reject(`Error loading text-to-image models: ${e.message || e.toString()}`);
		}
	};

	getMediums = async (): Promise<string[]> => {
		try {
			if (Object.keys(this.mediums).length === 0) {
				this.loadingMediums = true;
				const mediums: string[] = await this.queryService.get("/mediums");

				runInAction(() => {
					this.loadingMediums = false;
					this.mediums = mediums;
				});
			}
			return this.mediums;
		} catch (e: any) {
			this.loadingMediums = false;
			return Promise.reject(`Error loading text-to-image mediums: ${e.message || e.toString()}`);
		}
	};
	clearTailoredModelsSelections = () => {
		this.handleBulkConfigChange({
			tailored_model_id: undefined,
		});
	};
	clearFoundationModelsSelections = () => {
		this.handleBulkConfigChange({
			model: "",
			model_version: "",
		});
	};
	handleText2ImagePopupChange = (e: ChangeEvent<HTMLInputElement>, selectedOption: IGroupOption) => {
		const isTailoredModel = selectedOption && selectedOption?.extraData?.isTailoredModel;
		this.handleConfigChange("model_name", selectedOption.key);

		if (!isTailoredModel) {
			this.clearTailoredModelsSelections();
			this.handleBulkConfigChange({
				model: getModelName(e.target.value as string),
				model_version: getModelVersion(e.target.value as string),
			});
			if (selectedOption.value.toLowerCase().includes("fast")) {
				this.handleConfigChange("negative_prompt", "");
			}
		} else {
			this.clearFoundationModelsSelections();
			this.handleBulkConfigChange({
				tailored_model_id: selectedOption?.id,
				negative_prompt: undefined,
			});
			if (this.config.fast) {
				this.handleConfigChange("negative_prompt", "");
			}
		}
	};

	handleUploadImage = async (e: ChangeEvent<HTMLInputElement> | SyntheticEventProps) => {
		const file = e.target.files?.[0];
		e.target.value = "";

		if (file) {
			runInAction(() => {
				this.isUploadingImageReference = true;
			});

			const imageBeforeUpload: PlaygroundImage = {
				id: uuidv4(),
				url: URL.createObjectURL(file),
				file: file,
				type: "upload",
				offline_props: {
					prefix_id: file.name.split(".")[0],
					variation: 0,
				},
				playground_result_id: "",
			};

			runInAction(() => {
				this.imageReference = imageBeforeUpload;
				this.imageReferenceContentModerationError = false;
				this.config.medium = defaultTextToImageConfig.medium;
				if (
					IMAGE_REFERENCE_DISABLED_MODELS.includes(
						(this.config.model + "_" + this.config.model_version) as ModelDropdownKey,
					) &&
					!this.config.tailored_model_id
				) {
					this.config.model = defaultTextToImageConfig.model;
					this.config.model_version = defaultTextToImageConfig.model_version;
					this.config.model_name = defaultTextToImageConfig.model_name;
				}
				this.isUploadingImageReference = false;
			});
		}
	};

	handleDeleteImageReference = async () => {
		runInAction(() => {
			this.imageReference = { id: "", url: "", playground_result_id: "" };
			this.imageReferenceContentModerationError = false;
		});
	};

	refineImage = async (image: PlaygroundImage): Promise<void> => {
		const imageConfig = image.config as TextToImageConfigType;
		const isFiboGenerated = imageConfig?.model === "fibo" || !!image.structured_prompt;

		const requiresSeedAndStructured = isFiboGenerated && (!image.structured_prompt || image.seed === undefined);

		if (requiresSeedAndStructured) {
			throw new Error("Cannot refine: missing structured prompt or seed");
		}

		if (imageConfig?.training_version === "fibo") {
			await this.generateTailoredV2Multi({
				textToImageConfig: { ...imageConfig, refine_prompt: this.config.refine_prompt },
				isTgEvaluation: false,
				isRefine: true,
				image,
			});
		} else {
			await this.rootStore.fiboStore.refine({
				textToImageConfig: { ...imageConfig, refine_prompt: this.config.refine_prompt },
				isTgEvaluation: false,
				skipGenerateStructuredPrompt: isFiboGenerated,
				image,
			});
		}
	};
}

export const defaultTextToImageConfig: TextToImageConfigType = {
	model: "fibo",
	model_version: "",
	model_id: "fibo",
	model_name: "Bria Fibo",
	image_reference: IMAGE_REFERENCE,
	tailored_model_id: undefined,
	prompt: "",
	aspect_ratio: "4:3",
	medium: "",
	num_results: isFiboApp() ? FIBO_DEFAULT_NUM_RESULTS : 4,
	steps_num: 50,
	prompt_enhancement: true,
	enhance_image: false,
	content_moderation: true,
	text_guidance_scale: 5,
};
